import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FiCode, FiBriefcase, FiAward, FiZap } from 'react-icons/fi';

const highlights = [
  {
    icon: FiCode,
    title: 'Full-Stack Engineer',
    desc: 'React.js, Node.js, Python, Java — end-to-end product development.',
    color: 'bg-primary-50 text-primary-600 border-primary-100',
  },
  {
    icon: FiZap,
    title: 'AI/ML Practitioner',
    desc: 'GenAI, TensorFlow, PyTorch — integrating intelligence into real products.',
    color: 'bg-accent-50 text-accent-600 border-accent-100',
  },
  {
    icon: FiBriefcase,
    title: 'Enterprise Delivery',
    desc: '10+ projects for clients in healthcare, EdTech, e-commerce & FMCG.',
    color: 'bg-blue-50 text-blue-600 border-blue-100',
  },
  {
    icon: FiAward,
    title: 'National Awardee',
    desc: '2x National Runner-Up, Best Outgoing Student CSE 2024.',
    color: 'bg-yellow-50 text-yellow-600 border-yellow-100',
  },
];

function AnimateIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  return (
    <section id="about" className="bg-white">
      <div className="section-container">
        <AnimateIn>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">About Me</p>
          </div>
          <h2 className="section-title">Who I Am</h2>
          <p className="text-slate-400 text-sm mb-10">Engineer. Creator. Problem-solver.</p>
        </AnimateIn>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Text Content */}
          <div className="space-y-6">
            <AnimateIn delay={0.1}>
              <p className="text-slate-700 text-base leading-relaxed">
                I am a <span className="font-semibold text-primary-600">Software Development Engineer</span> with over 2 years of hands-on experience designing, building, and shipping production-grade software. From EdTech platforms serving 10,000+ Karnataka students to enterprise Sales Force Automation apps deployed to Raymond and other Fortune brands, I bring engineering precision to every layer of the stack.
              </p>
            </AnimateIn>
            <AnimateIn delay={0.15}>
              <p className="text-slate-600 text-base leading-relaxed">
                My work spans <strong>web, mobile, and AI/ML</strong> — I architect REST APIs, integrate Generative AI, automate deployments with Bash pipelines, and build intuitive user experiences with React Native and React.js. I thrive in cross-functional teams and take mentorship seriously, having led teams of developers to deliver clean, maintainable code under real deadlines.
              </p>
            </AnimateIn>
            <AnimateIn delay={0.2}>
              <p className="text-slate-600 text-base leading-relaxed">
                Recognized as the <span className="font-semibold text-slate-800">Best Outgoing CSE Student of 2024</span> by Member of Lok Sabha Anurag Thakur and a 2x National-level competition runner-up, I combine technical depth with a results-oriented mindset.
              </p>
            </AnimateIn>

            {/* Key Info Row */}
            <AnimateIn delay={0.25}>
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { label: 'Location', value: 'Bengaluru, KA' },
                  { label: 'Experience', value: '2+ Years' },
                  { label: 'Speciality', value: 'Full Stack & AI' },
                  { label: 'Education', value: 'B.E. Computer Science' },
                ].map((item) => (
                  <div key={item.label} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <p className="text-xs text-slate-400 font-medium">{item.label}</p>
                    <p className="text-sm text-slate-800 font-semibold mt-0.5">{item.value}</p>
                  </div>
                ))}
              </div>
            </AnimateIn>
          </div>

          {/* Highlight Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {highlights.map((h, i) => (
              <AnimateIn key={h.title} delay={0.15 + i * 0.08}>
                <div className={`card p-5 border ${h.color.split(' ').pop()}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${h.color.split(' ').slice(0, 2).join(' ')} mb-3`}>
                    <h.icon size={20} />
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">{h.title}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">{h.desc}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
